import { useEffect, useState } from 'react';
import { BarChart3, TrendingUp, Activity, AlertCircle } from 'lucide-react';
import { Badge, Card, CardBody, CardHeader, CardTitle } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { crmApi, ApiError } from '@solara/api-client';
import type { FunnelMetricRow, SalesForecastRow, VelocityDto } from '@solara/api-client';

export default function CrmReportsPage() {
  const { t } = useTranslation('p1');
  const [forecast, setForecast] = useState<SalesForecastRow[]>([]);
  const [funnel, setFunnel] = useState<FunnelMetricRow[]>([]);
  const [velocity, setVelocity] = useState<VelocityDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    try {
      const [f, fu, v] = await Promise.all([crmApi.forecast(), crmApi.funnel(), crmApi.velocity()]);
      setForecast(f);
      setFunnel(fu);
      setVelocity(v);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('crm.reportsTitle')}</h1>
        <p className="text-fg-muted text-sm">{t('crm.reportsSubtitle')}</p>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              {t('crm.forecastTitle')}
            </CardTitle>
          </CardHeader>
          <CardBody>
            {forecast.length === 0 ? (
              <p className="py-6 text-center text-sm text-fg-muted">{t('crm.noOpenOpportunities')}</p>
            ) : (
              <ul className="divide-y divide-border text-sm">
                {forecast.map((row) => (
                  <li key={row.ownerUserId} className="flex justify-between py-2">
                    <span className="font-mono text-xs">{row.ownerUserId.slice(0, 8)}</span>
                    <span>
                      {(Number(row.weightedAedMinor) / 100).toLocaleString()} AED ·{' '}
                      {row.opportunityCount} {t('crm.oppsShort')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              {t('crm.funnel')}
            </CardTitle>
          </CardHeader>
          <CardBody>
            <ul className="space-y-2 text-sm">
              {funnel.map((row) => (
                <li key={row.stage} className="flex items-center justify-between">
                  <span>{t(`crm.stages.${row.stage}`, { defaultValue: row.stage })}</span>
                  <span className="flex items-center gap-2">
                    <Badge tone="neutral" className="text-[10px]">
                      {row.count}
                    </Badge>
                    {row.conversionRate != null && (
                      <span className="text-xs text-fg-muted">{row.conversionRate}%</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      </div>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            {t('crm.pipelineVelocity')}
          </CardTitle>
        </CardHeader>
        <CardBody>
          {!velocity ? (
            <p className="text-sm text-fg-muted">{t('crm.noClosedDeals')}</p>
          ) : (
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <Item label={t('crm.avgDeal')} value={`${(Number(velocity.avgDealMinor) / 100).toLocaleString()} AED`} />
              <Item label={t('crm.winRate')} value={`${velocity.winRate}%`} />
              <Item label={t('crm.dealsClosed')} value={String(velocity.dealsClosed)} />
              <Item label={t('crm.avgCycle')} value={`${velocity.avgCycleDays} ${t('crm.days')}`} />
              <Item label={t('crm.velocityPerDay')} value={(Number(velocity.velocityAedMinorPerDay) / 100).toLocaleString()} highlight />
            </dl>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function Item({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={highlight ? 'col-span-2 rounded bg-surface-muted px-3 py-2' : ''}>
      <dt className="text-xs text-fg-muted">{label}</dt>
      <dd className="font-mono text-sm">{value}</dd>
    </div>
  );
}
